import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { BehaviorSubject, Observable, combineLatest, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { ProfilePhotoLoaderService } from "./profile-photo-loader.service";

@Component({
  standalone: false,
  selector: "app-profile-photo",
  templateUrl: "./profile-photo.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./profile-photo.component.css"],
})
export class ProfilePhotoComponent implements OnInit, OnChanges, OnDestroy {
  /** Lookup key (email / id) for the profile image */
  @Input() key = "";

  @ViewChild("imgEl", { static: true }) imgEl!: ElementRef<HTMLImageElement>;

  private readonly key$ = new BehaviorSubject<string>("");
  private readonly visible$ = new BehaviorSubject<boolean>(false);

  /** Stream used directly by the template via async pipe */
  dataUrl$!: Observable<SafeUrl | string>;

  private observer?: IntersectionObserver;
  private lastObjectUrl: string | null = null;

  constructor(
    private readonly loader: ProfilePhotoLoaderService,
    private readonly domSanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.setupIntersectionObserver();

    this.dataUrl$ = combineLatest([this.key$, this.visible$]).pipe(
      switchMap(([key, visible]) => {
        const fallback = "/assets/img/default-profile-photo.jpg";
        const normalized = (key ?? "").trim();

        if (!normalized) return of(fallback);

        // Lazy load: do not request until visible
        if (!visible) return of(fallback);

        return this.loader
          .getPhoto(normalized)
          .pipe(map((blob) => this.toSafeUrl(blob, fallback)));
      }),
    );
  }

  ngOnChanges(): void {
    this.key$.next(this.key);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.revokeObjectUrl();
    this.visible$.complete();
    this.key$.complete();
  }

  private setupIntersectionObserver(): void {
    // If IntersectionObserver isn't available, load immediately (rare).
    if (typeof IntersectionObserver === "undefined") {
      this.visible$.next(true);
      return;
    }

    // Reduce prefetch aggressiveness to avoid request bursts on large lists.
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries?.[0];
        const isVisible = !!entry?.isIntersecting;
        if (isVisible) {
          this.visible$.next(true);
          // Once visible, we can stop observing; photo stays loaded/cached.
          this.observer?.disconnect();
        }
      },
      { root: null, rootMargin: "50px", threshold: 0.1 },
    );

    // Observe the actual <img> element
    // ViewChild is static:true so it's available here.
    this.observer.observe(this.imgEl.nativeElement);
  }

  private toSafeUrl(blob: Blob | null, fallback: string): SafeUrl | string {
    if (!blob) {
      this.revokeObjectUrl();
      return fallback;
    }

    this.revokeObjectUrl();

    const url = URL.createObjectURL(blob);
    this.lastObjectUrl = url;

    return this.domSanitizer.bypassSecurityTrustUrl(url);
  }

  private revokeObjectUrl(): void {
    if (this.lastObjectUrl) {
      try {
        URL.revokeObjectURL(this.lastObjectUrl);
      } catch {
        // ignore
      }
      this.lastObjectUrl = null;
    }
  }
}
